import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {IStorefrontQuestion, IStorefrontSettings} from '../../models/Storefront';
import {
    useCreateQuoteRequestQuestionMutation,
    useDeleteQuoteRequestQuestionMutation,
    useGetQuoteRequestQuestionsQuery,
    useSaveQuoteRequestQuestionMutation
} from "../../store/endpoints/StorefrontQuestionEndpoints";
import StorefrontQuestionEditor from "./StorefrontQuestionEditor";
import {
    CommandBar,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    ICommandBarItemProps,
    IconButton,
    IContextualMenuProps,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack
} from "@fluentui/react";
import DataGrid from "../DataGrid/DataGrid";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";

interface IStorefrontQuoteRequestQuestionsFormProps {
    settings: IStorefrontSettings;
    onChange: (settings: IStorefrontSettings) => void;
}

const StorefrontQuoteRequestQuestionsForm: React.FC<IStorefrontQuoteRequestQuestionsFormProps> = (props) => {
    const quoteRequestQuestions = useGetQuoteRequestQuestionsQuery(props.settings.id ?? '', {skip: !props.settings.id || props.settings.id === ''});
    const [saver] = useSaveQuoteRequestQuestionMutation();
    const [createNewQuestion] = useCreateQuoteRequestQuestionMutation();
    const [deleter] = useDeleteQuoteRequestQuestionMutation();

    const [saving, setSaving] = useState(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [questionEditorError, setQuestionEditorError] = useState<string>();
    const [error, setError] = useState<string>();
    const [showEditor, setShowEditor] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<IStorefrontQuestion>();
    const [idToDelete, setIdToDelete] = useState<string>();

    useEffect(() => {
        return () => {
            setSaving(false);
            setIdToDelete(undefined);
            setSelectedQuestion(undefined);
            setError(undefined);
            setShowEditor(false);
            setQuestionEditorError(undefined);
        };
    }, []);

    /*
     * A per-question deep link names the question inside the setting id
     * (`storefront.quoteQuestion.<questionId>.<field>`), so open that question's editor rather than
     * leaving the panel closed.
     */
    const location = useLocation();
    const focusedQuestionId = React.useMemo(() => {
        const focus = new URLSearchParams(location.search).get('focus');
        return /^storefront\.quoteQuestion\.([^.]+)\./.exec(focus ?? '')?.[1];
    }, [location.search]);
    useEffect(() => {
        if (!focusedQuestionId) return;
        const match = quoteRequestQuestions.data?.items.find((q) => q.id === focusedQuestionId);
        if (match) {
            setSelectedQuestion(match);
            setShowEditor(true);
        }
    }, [focusedQuestionId, quoteRequestQuestions.data]);

    const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' });

    const getNewQuestionId = () => {
        const sequence = (quoteRequestQuestions.data?.items.length ?? 0) + 1;
        const id = `QuoteQuestion-${sequence}`;
        const exists = quoteRequestQuestions.data?.items?.some(a => a.identifier === id) ?? false;
        return exists ? crypto.randomUUID() : id;
    };

    const headerCommands: ICommandBarItemProps[] = [
        {
            key: 'addQuestion',
            text: 'Add Question',
            title: 'Add Question',
            disabled: saving,
            iconProps: {iconName: 'Add'},
            onClick: () => {
                if (props.settings.id) {
                    const newQuestion: IStorefrontQuestion = {
                        identifier: getNewQuestionId(),
                        questionText: '',
                        answers: [],
                        entityType: 'storefront',
                        entityId: props.settings.id
                    };
                    setSaving(true);
                    createNewQuestion({question: newQuestion, storefront: props.settings.id})
                        .unwrap()
                        .then(r => {
                            setError(undefined);
                            setSaving(false);
                            scrollToBottom();
                            setSelectedQuestion({...newQuestion, id: r.id});
                            setShowEditor(true);
                        })
                        .catch(err => {
                            setError(err?.data?.message ?? 'Unknown error creating question');
                            setSaving(false);
                        });
                }
            }
        }
    ];

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'saveAndClose',
                text: 'Save and Close',
                iconProps: {iconName: 'SaveAndClose'},
                onClick: () => { save(); setSelectedQuestion(undefined); setShowEditor(false); }
            }
        ]
    };

    const footer = () => (
        <div style={{background: 'white'}}>
            <Stack horizontal tokens={{childrenGap: '1em'}}>
                <PrimaryButton split menuProps={menuProps} text='Save' onClick={() => save()} disabled={saving} />
                <DefaultButton text='Cancel' onClick={() => { setSelectedQuestion(undefined); setShowEditor(false); setSaving(false); }} disabled={saving} />
            </Stack>
        </div>
    );

    const header = () => (
        <div style={{flexGrow: 1, paddingLeft: '25px', background: 'white'}}>
            <h1>Edit Quote Request Question</h1>
            {questionEditorError && <MessageBar messageBarType={MessageBarType.error}>{questionEditorError}</MessageBar>}
        </div>
    );

    const save = () => {
        setSaving(true);
        if (props.settings.id && selectedQuestion?.id) {
            saver({question: selectedQuestion, storefront: props.settings.id, questionId: selectedQuestion.id})
                .unwrap()
                .then(e => {
                    setQuestionEditorError(undefined);
                    if (!e.success) setQuestionEditorError(e.message ?? 'Unknown error saving question');
                    setSaving(false);
                })
                .catch(err => {
                    setQuestionEditorError(err?.data?.message ?? 'Unknown error saving question');
                    setSaving(false);
                });
        }
    };

    const deleteQuestion = (id: string) => {
        if (props.settings.id) {
            deleter({storefront: props.settings.id, questionId: id});
        }
        setIdToDelete(undefined);
    };

    const columns: IColumnDetails[] = [
        {
            index: 0, identifier: 'Edit', label: '', type: '', effectiveWidth: 50, required: true,
            component: (q: IStorefrontQuestion) => <IconButton iconProps={{iconName: 'edit'}} onClick={() => { setSelectedQuestion(q); setShowEditor(true); }} />
        },
        {
            identifier: 'identifier', label: 'Identifier', type: '', sortable: false, filterable: false, effectiveWidth: 200, required: true,
            component: (q: IStorefrontQuestion) => <div>{q.identifier}</div>
        },
        {
            identifier: 'questionText', label: 'Question Text', type: '', sortable: false, filterable: false, required: true,
            component: (q: IStorefrontQuestion) => <div>{q.questionText}</div>
        },
        {
            identifier: 'required', label: 'Required', type: '', sortable: false, filterable: false, required: true,
            component: (q: IStorefrontQuestion) => <div>{q.isRequired ? 'True' : 'False'}</div>
        },
        {
            identifier: 'Delete', label: '', type: '', effectiveWidth: 50, required: true,
            component: (q: IStorefrontQuestion) => <IconButton iconProps={{iconName: 'Delete'}} onClick={() => { if (q.id) { setIdToDelete(q.id); setShowConfirmDelete(true); } }} />
        }
    ];

    const panelStyles = {
        commands: {background: 'white'},
        footer: {background: 'white'},
        navigation: {flex: '1 1 auto'}
    };

    return (
        <div>
            <Dialog hidden={!showConfirmDelete} onDismiss={() => setShowConfirmDelete(false)}
                dialogContentProps={{type: DialogType.largeHeader, title: 'Delete Question', subText: 'Are you sure you want to delete this question?'}}
                modalProps={{isBlocking: true}}>
                <DialogFooter>
                    <DefaultButton primary text='Delete' onClick={() => { if (idToDelete) { deleteQuestion(idToDelete); setShowConfirmDelete(false); } }} />
                    <DefaultButton text='Cancel' onClick={() => setShowConfirmDelete(false)} />
                </DialogFooter>
            </Dialog>

            <Panel onOuterClick={() => undefined} type={PanelType.large}
                headerText='Edit Quote Request Question'
                isOpen={showEditor} onDismiss={() => setShowEditor(false)} closeButtonAriaLabel='Close'
                isFooterAtBottom={true} onRenderFooterContent={footer} onRenderHeader={header} styles={panelStyles}>
                <StorefrontQuestionEditor
                    question={selectedQuestion}
                    canEdit={true}
                    settingKeyPrefix='storefront.quoteQuestion'
                    onChange={q => setSelectedQuestion({...q})}
                />
            </Panel>

            {(quoteRequestQuestions.isFetching || quoteRequestQuestions.isLoading)
                ? <Spinner label='Loading Questions...' size={SpinnerSize.large} />
                : <>
                    {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                    <CommandBar items={headerCommands} farItems={[]} />
                    <DataGrid
                        getItemId={(item: IStorefrontQuestion) => item.id ?? ''}
                        items={quoteRequestQuestions.data?.items ?? []}
                        selectionMode={GridSelectionMode.none}
                        loading={quoteRequestQuestions.isFetching || quoteRequestQuestions.isLoading}
                        overrideColumns={columns}
                        useContentHeight={true}
                    />
                </>
            }
            <div ref={bottomRef} />
        </div>
    );
};

export default StorefrontQuoteRequestQuestionsForm;
